import React from 'react'
import { useHistory } from 'react-router-dom'
import Datalist from 'components/Datalist'
import { useCompanies, useRemoveCompanyMember } from '@modmd/data'
import { ROUTES } from 'internal-portal/constants/routes'
import { DEVICE, RADIUS } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Card from 'components/Card'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { useToaster } from 'utils/useToaster'

export interface Params {
  userId: string
}

const SKELETON_COLUMNS_COMPANIES = 2
const SKELETON_ROWS_COMPANIES = 5

export const CompaniesUser = ({ userId }: Params) => {
  const history = useHistory()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [areUserCompaniesRefetching, setAreUserCompaniesRefetching] = React.useState(false)
  const { setToastMessage } = useToaster()

  const {
    data: userCompanies,
    loading: areUserCompaniesLoading,
    refetch: refetchUserCompanies,
  } = useCompanies({
    filter: {
      User: { id: userId },
    },
    membersFilterData: {
      id: userId,
    },
    membersPageLength: 1,
    pageLength: 999,
    skipMembers: false,
  })
  const { removeCompanyMember, isRemoveCompanyMemberLoading } = useRemoveCompanyMember()
  const handleRemoveCompanyMember = React.useCallback(
    async ({ companyId }: { companyId: string }) => {
      if (window.confirm(`Are you sure you want to remove user from this company?`)) {
        try {
          await removeCompanyMember({
            variables: {
              inputData: {
                companyId,
                userId,
              },
            },
          })
          setAreUserCompaniesRefetching(true)
          void refetchUserCompanies()
          setAreUserCompaniesRefetching(false)
          setToastMessage('User successfully deleted', 'success')
        } catch {
          // ignore
        }
      }
    },
    [refetchUserCompanies, removeCompanyMember, setToastMessage, userId]
  )

  return (
    <Card borderRadius={`0 0 ${RADIUS.RADIUS_5} ${RADIUS.RADIUS_5}`} paddingBottom={5}>
      <Datalist
        data={userCompanies?.companies.companies ?? []}
        isLoading={areUserCompaniesLoading || areUserCompaniesRefetching}
        skeletonProps={{
          columns: SKELETON_COLUMNS_COMPANIES,
          rows: SKELETON_ROWS_COMPANIES,
        }}
        shouldRenderTableHeader={isDesktop}
        showActionIndicators
        renderTableHeader={() => (
          <Datalist.Row gridTemplateColumns="repeat(3, 1fr)">
            <Datalist.HeaderCell label="Name" />
            <Datalist.HeaderCell label="Role" />
            <Datalist.HeaderCell label="Action" />
          </Datalist.Row>
        )}
        renderTableRow={({ id, name, Members }) => (
          <Datalist.Row
            gridTemplateColumns="repeat(3, 1fr)"
            onClick={() => history.push(`/${ROUTES.COMPANIES}/${id}`)}
          >
            <Datalist.Cell label="Name">{name}</Datalist.Cell>
            <Datalist.Cell label="Role">
              {Members?.Members?.find(({ User }) => User.id === userId)?.role}
            </Datalist.Cell>
            <Datalist.Cell label="Actions">
              <Button
                size="small"
                isFetching={isRemoveCompanyMemberLoading}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleRemoveCompanyMember({ companyId: id })
                }}
                appearance="ghost"
                colorVariant="danger"
              >
                <Icon.Trash />
              </Button>
            </Datalist.Cell>
          </Datalist.Row>
        )}
      />
    </Card>
  )
}
